import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

/**
 * Route Navigation Data Interface
 */
export interface RouteNavigationData {
  id?: string;
  code?: string;
  name?: string;
  routeCode?: string;
  routeName?: string;
  externalKeys?: {
    code?: string;
  };
}

/**
 * Route Query Parameters Interface
 */
export interface RouteQueryParams {
  search?: string;
  filterSubRouteCodes?: string;
  companyId?: string;
  status?: string;
  currentPage?: number;
  limitPerPage?: number;
  sort?: string;
  sortDirection?: 'asc' | 'desc';
  [key: string]: any;
}

/**
 * Route Navigation Event Interface
 */
export interface RouteNavigationEvent {
  data: RouteNavigationData;
  queryParams: RouteQueryParams;
  timestamp?: Date;
  source?: string;
}

/**
 * Sub-Route Navigation Service
 * Centralizes navigation logic for Sub-Route related screens
 * Provides programmatic navigation capabilities similar to BusinessPartnerNavigationService
 * Note: In this project, "sub-route" is the actual route entity
 */
@Injectable({
  providedIn: 'root'
})
export class SubRouteNavigationService {
  /** Base path for Sub-Route screens (subroute is the route in this project) */
  private readonly basePath: string[] = ['master', 'routing', 'sub-route'];

  private readonly dialog = inject(MatDialog);

  constructor(private router: Router) {}

  /**
   * Navigate to Sub-Route list with optional filters
   * @param queryParams - Query parameters for filtering
   * @param openInNewTab - Whether to open in new tab (default: true)
   */
  navigateToRouteList(
    queryParams: RouteQueryParams = {},
    openInNewTab: boolean = true
  ): void {
    const urlTree = this.router.createUrlTree(this.basePath, { queryParams });

    if (openInNewTab) {
      const url = window.location.origin + this.router.serializeUrl(urlTree);
      window.open(url, '_blank');
    } else {
      // Same-tab navigation replaces the current page, so close any open dialog
      // (e.g. the document detail modal) that would otherwise float on top.
      this.dialog.closeAll();
      this.router.navigateByUrl(urlTree);
    }
  }

  /**
   * Navigate to Sub-Route list filtered by specific Route code
   * Uses filterSubRouteCodes parameter for precise code filtering
   * @param route - Route data to filter by
   * @param additionalParams - Additional query parameters
   * @param openInNewTab - Whether to open in new tab (default: true)
   */
  navigateToRouteDetail(
    route: RouteNavigationData,
    additionalParams: RouteQueryParams = {},
    openInNewTab: boolean = true
  ): void {
    const routeCode = this.getRouteCode(route);
    const queryParams: RouteQueryParams = {
      filterSubRouteCodes: routeCode,
      ...additionalParams
    };

    this.navigateToRouteList(queryParams, openInNewTab);
  }

  /**
   * Navigate with pre-built navigation event
   * @param navigationEvent - Complete navigation event with data and params
   */
  navigateFromEvent(navigationEvent: RouteNavigationEvent): void {
    this.navigateToRouteDetail(
      navigationEvent.data,
      navigationEvent.queryParams,
      true // Always open in new tab for programmatic navigation
    );
  }

  /**
   * Create Sub-Route URL without navigating
   * @param queryParams - Query parameters for the URL
   * @returns Complete URL string
   */
  createRouteUrl(queryParams: RouteQueryParams = {}): string {
    const urlTree = this.router.createUrlTree(this.basePath, { queryParams });
    return window.location.origin + this.router.serializeUrl(urlTree);
  }

  /**
   * Create Sub-Route detail URL for specific route code
   * Uses filterSubRouteCodes parameter for precise code filtering
   * @param route - Route data
   * @param additionalParams - Additional query parameters
   * @returns Complete URL string
   */
  createRouteDetailUrl(
    route: RouteNavigationData,
    additionalParams: RouteQueryParams = {}
  ): string {
    const routeCode = this.getRouteCode(route);
    const queryParams: RouteQueryParams = {
      filterSubRouteCodes: routeCode,
      ...additionalParams
    };

    return this.createRouteUrl(queryParams);
  }

  /**
   * Check if Route is navigable
   * @param route - Route data
   * @returns True if navigable, false otherwise
   */
  isRouteNavigable(route: RouteNavigationData): boolean {
    if (!route) return false;

    // Check if we have any identifiable information
    const hasId = !!route.id;
    const hasCode = !!route.code;
    const hasRouteCode = !!route.routeCode;
    const hasExternalCode = !!route.externalKeys?.code;
    const hasName = !!route.name || !!route.routeName;

    return hasId || hasCode || hasRouteCode || hasExternalCode || hasName;
  }

  /**
   * Get description for navigation tooltip
   * @returns Description string for tooltip
   */
  getNavigationDescription(): string {
    return 'Ver detalle de Ruta';
  }

  /**
   * Get the route code for filtering
   * Prioritizes external code, then routeCode, then code, then name
   * @param route - Route data
   * @returns Route code string
   */
  private getRouteCode(route: RouteNavigationData): string {
    if (!route) return '';

    // Priority order: external code, routeCode, code, name
    const externalCode = route.externalKeys?.code;
    const routeCode = route.routeCode;
    const code = route.code;
    const name = route.routeName || route.name;

    return externalCode || routeCode || code || name || '';
  }

  /**
   * Validate query parameters to ensure only valid ones are used
   * @param queryParams - Raw query parameters
   * @returns Validated and cleaned query parameters
   */
  validateQueryParams(queryParams: RouteQueryParams): RouteQueryParams {
    const validatedParams: RouteQueryParams = {};

    // Only include defined values to avoid empty query params
    const validKeys: (keyof RouteQueryParams)[] = [
      'search',
      'filterSubRouteCodes',
      'companyId',
      'status',
      'currentPage',
      'limitPerPage',
      'sort',
      'sortDirection'
    ];

    for (const key of validKeys) {
      const value = queryParams[key];
      if (value !== undefined && value !== null && value !== '') {
        // For arrays, only include non-empty arrays
        if (Array.isArray(value) && value.length > 0) {
          validatedParams[key] = value;
        } else if (!Array.isArray(value)) {
          validatedParams[key] = value;
        }
      }
    }

    return validatedParams;
  }

  /**
   * Create a navigation event
   * @param data - Route data
   * @param queryParams - Query parameters
   * @param source - Source of the navigation
   * @returns Navigation event object
   */
  createNavigationEvent(
    data: RouteNavigationData,
    queryParams: RouteQueryParams = {},
    source: string = 'general'
  ): RouteNavigationEvent {
    return {
      data,
      queryParams: this.validateQueryParams(queryParams),
      timestamp: new Date(),
      source
    };
  }
}
